import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { systemClock } from '@reos/config';
import { useT } from '../lib/i18n.js';
import { listNewMatches } from '../lib/matches-api.js';
import { errorMessage } from '../lib/properties.js';
import { formatFullDate } from '../lib/relative-time.js';
import { V4Group, V4Header, V4Pill, V4Row } from '../components/ux-v4.js';

/**
 * `/parovani-poptavek` (krok CRM4, bod 3) — všechny NOVÉ shody poptávek
 * s nabídkami (Dnešek ukazuje nejvýš 3 a odkazuje sem). Nabídka se posílá
 * z karty nemovitosti (náhled + potvrzení), proto řádek vede tam.
 */
const ALL_NEW_MATCHES_LIMIT = 50;

export function ParovaniPoptavekPage(): React.JSX.Element {
  const t = useT();
  const [now] = React.useState(() => systemClock.now());
  const matches = useQuery({
    queryKey: ['new-matches', ALL_NEW_MATCHES_LIMIT],
    queryFn: () => listNewMatches(ALL_NEW_MATCHES_LIMIT),
  });
  const items = matches.data?.items ?? [];
  return (
    <div className="rh-v4-page" data-testid="parovani-page">
      <V4Header date={formatFullDate(now)} title={t('webAdmin.matches.pageTitle')} lede={t('webAdmin.matches.pageLede')} />
      {matches.isLoading ? (
        <p role="status" className="rh-a2-status">
          {t('webAdmin.matches.loading')}
        </p>
      ) : null}
      {matches.error !== null ? (
        <p role="alert" className="rh-a2-error">
          {errorMessage(matches.error)}
        </p>
      ) : null}
      {matches.data !== undefined && items.length === 0 ? (
        <div className="rh-a2-empty" data-testid="parovani-empty">
          <p>{t('webAdmin.matches.pageEmpty')}</p>
          <p>
            <Link to="/poptavky" data-testid="parovani-empty-demands">
              {t('webAdmin.matches.pageEmptyLinkDemands')}
            </Link>
            {' · '}
            <Link to="/nemovitosti" data-testid="parovani-empty-properties">
              {t('webAdmin.matches.pageEmptyLinkProperties')}
            </Link>
          </p>
        </div>
      ) : null}
      {items.length > 0 ? (
        <V4Group testId="parovani-list" label={t('webAdmin.matches.pageTitle')}>
          {items.map((m) => (
            <V4Row
              key={`${m.demandId}:${m.propertyId}`}
              title={`${m.demandTitle} → ${m.propertyLabel ?? t('webAdmin.matches.unnamedProperty')}`}
              sub={m.explanation}
              right={<V4Pill tone={m.score >= 80 ? 'accent' : 'neutral'}>{t('webAdmin.matches.score', { score: m.score })}</V4Pill>}
              chevron
              render={(className, content) => (
                <Link
                  to="/nemovitosti/$id"
                  params={{ id: m.propertyId }}
                  className={className}
                  data-testid={`parovani-${m.demandId}-${m.propertyId}`}
                >
                  {content}
                </Link>
              )}
            />
          ))}
        </V4Group>
      ) : null}
    </div>
  );
}
